import { useCallback, useMemo } from 'react'
import { generatePath, useParams } from 'react-router-dom'

import { Layout } from 'admin/lib/Layouts'
import { Breadcrumb, BreadcrumbArrayItem } from 'admin/lib/Breadcrumbs'
import { useApiQuery } from 'admin/lib/Api'
import { EditProcore } from './EditProcore'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'
import { Alert } from 'antd'

export type Project = {
  projectId: string
  name: string
  procoreConfiguration: ProjectProcoreConfiguration | null
}

export type ProjectProcoreConfiguration = {
  companyId: string
  companyName: string
  projectId: string
  projectName: string
  projectNumber: string | null
  isAlertSyncEnabled: boolean
}

export type ProcoreCompany = {
  companyId: string
  name: string
  logoUrl: string | null
}
export type ProcoreProject = {
  projectId: string
  name: string
  projectNumber: string | null
  countryCode: string | null
  streetAddress: string | null
  city: string | null
  county: string | null
  zipCode: string | null
  startDate: string
  completionDate: string
  isDemo: boolean
}

type Account = {
  name: string
}

type Context = {
  loaded: boolean
  account: Account | null
  projects: Project[] | null
  procoreCompanies: ProcoreCompany[] | null
  loadingError: string | null
  loadingErrorFor: string | null
}

export function Procore() {
  const { accountId } = useParams<{
    accountId: string
  }>()

  const account = useApiQuery(
    useCallback(
      api => {
        if (!accountId) {
          throw new Error('Account ID is required')
        }
        return api.getAccount(accountId)
      },
      [accountId]
    )
  )

  const projects = useApiQuery(
    useCallback(
      api => {
        if (!accountId) {
          throw new Error('Account ID is required')
        }
        return api.listProjects(accountId)
      },
      [accountId]
    )
  )

  const procoreCompanies = useApiQuery(
    useCallback(api => {
      return api.getProcoreCompanies()
    }, [])
  )

  const context = useMemo(() => {
    return {
      loaded: account.loaded && projects.loaded && procoreCompanies.loaded,
      account: account.data,
      projects: projects.data,
      procoreCompanies: procoreCompanies.data,
      loadingError: account.error || projects.error || procoreCompanies.error,
      loadingErrorFor: account.error
        ? 'Account'
        : projects.error
          ? 'Account Projects'
          : procoreCompanies.error
            ? 'Procore Companies'
            : null
    } as Context
  }, [
    account.data,
    account.error,
    account.loaded,
    procoreCompanies.data,
    procoreCompanies.error,
    procoreCompanies.loaded,
    projects.data,
    projects.error,
    projects.loaded
  ])

  return (
    <Layout
      header={
        <Breadcrumb
          items={[
            {
              title: (
                <BreadcrumbArrayItem
                  href={generatePath(ROUTES.ACCOUNTS.LIST)}
                  loading={!account.loaded}
                >
                  Accounts
                </BreadcrumbArrayItem>
              )
            },
            {
              title: (
                <BreadcrumbArrayItem
                  loading={!context.loaded}
                  href={generatePath(ROUTES.PROJECTS.LIST, {
                    accountId: accountId
                  })}
                >
                  {context.account?.name}
                </BreadcrumbArrayItem>
              )
            },
            {
              title: 'Procore Integration'
            }
          ]}
        />
      }
    >
      <>
        {!context.loaded && <ScreenSpin />}

        {context.loadingError && (
          <Alert
            type="error"
            message={`${context.loadingErrorFor} failed to load`}
            description={context.loadingError}
            showIcon
            style={{ marginTop: '20px' }}
          />
        )}

        {context.loaded &&
          !context.loadingError &&
          context.projects &&
          context.procoreCompanies && (
            <EditProcore
              projects={context.projects}
              procoreCompanies={context.procoreCompanies}
            />
          )}
      </>
    </Layout>
  )
}
